import {
  LicenseCompanyHeaderRenderer,
  LicenseDetailCellRenderer,
  LicenseLastLoginCellRenderer,
  LicenseLastLoginHeaderRenderer,
  LicenseStatusCellRenderer,
  LicenseStatusHeaderRenderer,
  LicenseUserHeaderRenderer,
} from "./LicenseManagementTableRenderer";

import {
  HIDDEN_LICENSE_IDS,
  LICENSE_TYPES,
  licenseActionMenuKeys,
} from "../../../constants/LicenseManagement.constants";

import {
  CalendarIcon,
  FileProtectIcon,
  MinusCircleIcon,
  UserIcon,
} from "../../../assets/icons";

const getLicenseChildren = (
  licenseType,
  licenseData,
  onStatusFilterChange,
  currentLicenseFilters,
  showSubheaderFilters,
) => {
  const groupItems = (licenseData || []).filter(
    (item) =>
      item.licenseType === licenseType && !HIDDEN_LICENSE_IDS.includes(item.id),
  );

  return groupItems.reduce((acc, item, idx, arr) => {
    const { name: licenseHeaderName, id } = item;
    const isLastInGroup = groupItems[groupItems.length - 1].id === id;
    acc.push({
      headerName: licenseHeaderName,
      headerComponent: LicenseStatusHeaderRenderer,
      headerComponentParams: {
        displayName: licenseHeaderName,
        licenseId: id,
        licenseType: licenseType,
        licenseName: licenseHeaderName,
        onFilterClick: onStatusFilterChange || (() => {}),
        currentFilters: currentLicenseFilters || [],
        showFilterIcon: showSubheaderFilters,
        id: `license-header-status-${licenseType}-${id}`,
      },
      field: `${id}`,
      width: 120,
      cellRenderer: LicenseStatusCellRenderer,
      cellRendererParams: {
        colId: "status-column",
        licenseType,
        licenseId: id,
      },
      cellClass: isLastInGroup
        ? "license-status-cell-center license-status-cell-last-in-group"
        : "license-status-cell-center",
      headerClass: "license-status-header",
    });
    return acc;
  }, []);
};

export function getLicenseManagementTableConfig(
  licenseData,
  onRoleFilterChange,
  onStatusFilterChange,
  currentLicenseFilters,
  currentRoleFilters,
  showSubheaderFilters = true,
  onSortChange,
  currentSortDirection,
  currentSortBy,
  onCompanySearchChange,
  companySearch,
  onActionMenuOption,
  onDateRangeChange,
  fromDate,
  toDate,
) {
  return [
    {
      id: 1,
      headerName: "licenseManagement.userColumn",
      field: "name",
      cellRenderer: LicenseDetailCellRenderer,
      cellRendererParams: {
        colId: "user-column",
      },
      width: 160,
      pinned: "left",
      lockPosition: "left",
      cellClass: "user-column-cell",
      headerComponent: LicenseUserHeaderRenderer,
      headerComponentParams: {
        displayName: "licenseManagement.userColumn",
        sortDirection: currentSortBy === "name" ? currentSortDirection : null,
        onSortClick: onSortChange || (() => {}),
        id: "license-header-user-column",
      },
    },
    {
      id: 2,
      headerName: "licenseManagement.emailColumn",
      field: "email",
      cellRenderer: LicenseDetailCellRenderer,
      cellRendererParams: {
        colId: "email-column",
      },
      width: 220,
    },
    {
      id: 3,
      headerName: "licenseManagement.publicColumn",
      width: 420,
      marryChildren: false,
      headerClass: "license-main-staus-header",
      headerComponentParams: {
        id: "license-header-public-column",
      },
      children: getLicenseChildren(
        LICENSE_TYPES.public,
        licenseData,
        onStatusFilterChange,
        currentLicenseFilters,
        showSubheaderFilters,
      ),
    },
    {
      id: 4,
      headerName: "licenseManagement.dupontColumn",
      width: 420,
      marryChildren: true,
      headerClass: "license-main-staus-header",
      headerComponentParams: {
        id: "license-header-dupont-column",
      },
      children: getLicenseChildren(
        LICENSE_TYPES.duPont,
        licenseData,
        onStatusFilterChange,
        currentLicenseFilters,
        showSubheaderFilters,
      ),
    },
    {
      id: 5,
      headerName: "licenseManagement.roSpecificColumn",
      width: 420,
      marryChildren: true,
      headerClass: "license-main-staus-header",
      headerComponentParams: {
        id: "license-header-ro-specific-column",
      },
      children: getLicenseChildren(
        LICENSE_TYPES.roSpecific,
        licenseData,
        onStatusFilterChange,
        currentLicenseFilters,
        showSubheaderFilters,
      ),
    },
    {
      id: 6,
      headerName: "licenseManagement.companyColumn",
      field: "companyName",
      width: 190,
      cellRenderer: LicenseDetailCellRenderer,
      cellRendererParams: {
        colId: "company-column",
      },
      headerComponent: LicenseCompanyHeaderRenderer,
      headerComponentParams: {
        displayName: "licenseManagement.companyColumn",
        onSearch: onCompanySearchChange,
        currentSearch: companySearch,
        id: "company-column",
      },
    },
    {
      id: 7,
      headerName: "licenseManagement.lastLoginColumn",
      field: "lastLogin",
      width: 175,
      cellRenderer: (params) => (
        <LicenseLastLoginCellRenderer
          data={params.data}
          onActionMenuOption={onActionMenuOption}
          colId="last-login"
          node={params.node}
        />
      ),
      pinned: "right",
      lockPosition: "right",
      headerComponent: LicenseLastLoginHeaderRenderer,
      headerComponentParams: {
        displayName: "licenseManagement.lastLoginColumn",
        sortDirection:
          currentSortBy === "lastLogin" ? currentSortDirection : null,
        onSortClick: onSortChange || (() => {}),
        onDateRangeChange: onDateRangeChange || (() => {}),
        fromDate: fromDate || null,
        toDate: toDate || null,
        id: "license-header-last-login-column",
      },
    },
  ];
}

export const getLicenseActionMenuOptions = () => [
  {
    id: licenseActionMenuKeys.VIEW_USER,
    label: "licenseManagement.viewUserMenu",
    icon: <UserIcon />,
  },
  {
    id: licenseActionMenuKeys.ASSIGN_LICENSE,
    label: "licenseManagement.assignLicenseMenu",
    icon: <FileProtectIcon />,
  },
  {
    id: licenseActionMenuKeys.RENEW_LICENSE,
    label: "licenseManagement.renewLicenseMenu",
    icon: <CalendarIcon />,
  },
  {
    id: licenseActionMenuKeys.TERMINATE_LICENSE,
    label: "licenseManagement.terminateImmediatelyMenu",
    icon: <MinusCircleIcon />,
  },
];

export const LICENSE_ACTION_MENU_OPTIONS = getLicenseActionMenuOptions();
